import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom'; // ✅ Ajouté pour récupérer le message de redirection
import { Card, Button, Form, Alert, Spinner, Row, Col } from 'react-bootstrap';
import { AdminSidebar } from '../../components/admin';
import { TwoFactorManager } from '../../components/auth';
import api from '../../services/api'; 
import '../../components/admin/AdminLayout.css';

const AdminProfile = () => {
  const location = useLocation(); // ✅ Pour lire le state envoyé par Login.js
  const [form, setForm] = useState({ name: '', email: '' });
  const [editMode, setEditMode] = useState(false);
  const [pwMode, setPwMode] = useState(false);
  const [pwForm, setPwForm] = useState({ oldPassword: '', newPassword: '', confirmPassword: '' });
  const [status, setStatus] = useState({ type: '', msg: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // ✅ Si on vient de la page Login avec un message (Activation compte)
    if (location.state?.message) {
      setStatus({ type: 'warning', msg: location.state.message });
      setPwMode(true); // Ouvre directement le formulaire de mot de passe
    }

    const fetchProfile = async () => {
      try {
        const res = await api.get('/auth/me'); 
        setForm({ name: res.data.name, email: res.data.email });
      } catch (err) {
        setStatus({ type: 'danger', msg: "Session expirée. Veuillez vous reconnecter." });
      } finally { setLoading(false); }
    };
    fetchProfile();
  }, [location]);

  const handleProfileSave = async e => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put('/admin/profile', form); // ✅ Route à créer en Backend
      setStatus({ type: 'success', msg: 'Profil mis à jour.' });
      setEditMode(false);
    } catch (err) {
      setStatus({ type: 'danger', msg: err.response?.data?.error || 'Erreur de mise à jour.' });
    } finally { setSaving(false); }
  };

  const handlePwSave = async e => {
    e.preventDefault();
    if (pwForm.newPassword !== pwForm.confirmPassword) {
      setStatus({ type: 'danger', msg: 'Les mots de passe ne correspondent pas.' });
      return;
    }
    setSaving(true);
    try {
      // ✅ Envoi au backend : Spring va hacher le password en BCrypt
      await api.put('/auth/change-password', {
        oldPassword: pwForm.oldPassword,
        newPassword: pwForm.newPassword
      });
      setStatus({ type: 'success', msg: 'Votre compte est désormais activé avec votre nouveau mot de passe !' });
      setPwMode(false);
      setPwForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      setStatus({ type: 'danger', msg: err.response?.data?.error || 'Erreur : vérifiez votre mot de passe actuel.' });
    } finally { setSaving(false); }
  };

  if (loading) return <div className="text-center p-5"><Spinner animation="border" variant="danger" /></div>;

  return (
    <div className="admin-profile-container">
      <AdminSidebar />
      <div className="admin-content">
        <h2 className="mb-4">Gestion du Profil</h2>
        
        {status.msg && <Alert variant={status.type} className="shadow-sm">{status.msg}</Alert>}
        
        {/* Section Informations Personnelles */}
        <Card className="mb-4 shadow-sm border-0" style={{borderRadius: '15px'}}>
          <Card.Header className="bg-white fw-bold">Informations personnelles</Card.Header>
          <Card.Body>
            <Form onSubmit={handleProfileSave}>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Nom</Form.Label>
                    <Form.Control value={form.name} onChange={e => setForm({...form, name: e.target.value})} disabled={!editMode} required />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Email (Identifiant)</Form.Label>
                    <Form.Control value={form.email} disabled />
                  </Form.Group>
                </Col>
              </Row>
              {!editMode ? (
                <Button variant="outline-primary" size="sm" onClick={() => setEditMode(true)}>Modifier les informations</Button>
              ) : (
                <div className="d-flex gap-2">
                  <Button type="submit" variant="primary" size="sm" disabled={saving}>Sauvegarder</Button>
                  <Button variant="secondary" size="sm" onClick={() => setEditMode(false)}>Annuler</Button>
                </div>
              )}
            </Form>
          </Card.Body>
        </Card>

        {/* Section Mot de passe (Activation) */}
        <Card className="shadow-sm border-0 mb-4" style={{borderRadius: '15px', borderLeft: pwMode ? '5px solid #d90000' : 'none'}}>
          <Card.Body>
            <h5 className="fw-bold">Sécurité & Mot de passe</h5>
            <p className="text-muted small">Nous vous conseillons d'utiliser un mot de passe robuste (12+ caractères).</p>
            
            {!pwMode ? (
              <Button variant="danger" size="sm" onClick={() => setPwMode(true)}>Changer le mot de passe</Button>
            ) : (
              <Form onSubmit={handlePwSave} className="mt-3">
                <Form.Group className="mb-3">
                  <Form.Label>Mot de passe actuel (provisoire)</Form.Label>
                  <Form.Control type="password" onChange={e => setPwForm({...pwForm, oldPassword: e.target.value})} required />
                </Form.Group>
                <hr />
                <Form.Group className="mb-3">
                  <Form.Label>Nouveau mot de passe</Form.Label>
                  <Form.Control type="password" onChange={e => setPwForm({...pwForm, newPassword: e.target.value})} required minLength={12} placeholder="Min. 12 caractères" />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Confirmer le mot de passe</Form.Label>
                  <Form.Control type="password" onChange={e => setPwForm({...pwForm, confirmPassword: e.target.value})} required />
                </Form.Group>
                <div className="d-flex gap-2">
                  <Button type="submit" variant="danger" size="sm" disabled={saving}>Activer le nouveau mot de passe</Button>
                  <Button variant="secondary" size="sm" onClick={() => setPwMode(false)}>Annuler</Button>
                </div>
              </Form>
            )}
          </Card.Body>
        </Card>
        
        <TwoFactorManager />
      </div>
    </div>
  );
};

export default AdminProfile;